import { useEffect } from 'react'
import type { SudokuPuzzle } from '../../types'
import { BOX_DIMS } from '../../lib/sudoku/generator'

interface SudokuGridProps {
  puzzle:   SudokuPuzzle
  user:     number[]
  selected: number        // cell index, -1 if none
  checked:  boolean       // show correct/wrong highlights
  revealed: boolean       // show full solution
  onSelect: (i: number) => void
  onInput:  (i: number, v: number) => void
}

export default function SudokuGrid({
  puzzle,
  user,
  selected,
  checked,
  revealed,
  onSelect,
  onInput,
}: SudokuGridProps) {
  const { size } = puzzle.config
  const { w: boxW, h: boxH } = BOX_DIMS[size]
  const nums = Array.from({ length: size }, (_, i) => i + 1)

  // ── Keyboard navigation ──────────────────────────────────────────────────

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (selected < 0) return

      const isGiven = puzzle.given[selected] !== 0
      const n = parseInt(e.key)

      if (!isNaN(n) && n >= 1 && n <= size && !isGiven) {
        e.preventDefault()
        onInput(selected, n)
      } else if ((e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') && !isGiven) {
        e.preventDefault()
        onInput(selected, 0)
      } else if (e.key === 'ArrowRight' && selected % size < size - 1) {
        e.preventDefault(); onSelect(selected + 1)
      } else if (e.key === 'ArrowLeft' && selected % size > 0) {
        e.preventDefault(); onSelect(selected - 1)
      } else if (e.key === 'ArrowDown' && selected + size < size * size) {
        e.preventDefault(); onSelect(selected + size)
      } else if (e.key === 'ArrowUp' && selected - size >= 0) {
        e.preventDefault(); onSelect(selected - size)
      }
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [selected, size, puzzle.given, onInput, onSelect])

  // ── Cell helpers ─────────────────────────────────────────────────────────

  const selRow = selected >= 0 ? Math.floor(selected / size) : -1
  const selCol = selected >= 0 ? selected % size : -1

  function getCellBg(idx: number): string {
    const row = Math.floor(idx / size)
    const col = idx % size
    const isGiven    = puzzle.given[idx] !== 0
    const userVal    = user[idx]
    const isSelected = idx === selected
    const isRelated  = selected >= 0 && !isSelected && (
      row === selRow ||
      col === selCol ||
      (Math.floor(row / boxH) === Math.floor(selRow / boxH) &&
       Math.floor(col / boxW) === Math.floor(selCol / boxW))
    )
    const isCorrect  = checked && !isGiven && userVal !== 0 && userVal === puzzle.solution[idx]
    const isWrong    = checked && !isGiven && userVal !== 0 && userVal !== puzzle.solution[idx]

    if (isCorrect)    return '#D1FAE5'  // green
    if (isWrong)      return '#FEE2E2'  // red
    if (isSelected)   return '#FFE5E5'  // coral-light
    if (isRelated)    return '#FFF5F5'  // very subtle tint
    if (isGiven)      return '#F7F7F7'  // given cells
    return '#FFFFFF'
  }

  function getCellColor(idx: number): string {
    const isGiven   = puzzle.given[idx] !== 0
    const userVal   = user[idx]
    const isCorrect = checked && !isGiven && userVal !== 0 && userVal === puzzle.solution[idx]
    const isWrong   = checked && !isGiven && userVal !== 0 && userVal !== puzzle.solution[idx]

    if (revealed)   return '#2D2D2D'
    if (isGiven)    return '#2D2D2D'
    if (isCorrect)  return '#16A34A'
    if (isWrong)    return '#DC2626'
    return '#FF6B6B'  // user-filled — coral
  }

  const fontSize =
    size === 4 ? '1.7rem' :
    size === 6 ? '1.35rem' :
    '1.1rem'

  const maxWidth =
    size === 4 ? 240 :
    size === 6 ? 330 :
    420

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Grid */}
      <div
        className="mx-auto select-none"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${size}, 1fr)`,
          maxWidth,
          border: '3px solid #2D2D2D',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        {puzzle.given.map((given, idx) => {
          const row = Math.floor(idx / size)
          const col = idx % size
          const isBoxRight  = (col + 1) % boxW === 0 && col < size - 1
          const isBoxBottom = (row + 1) % boxH === 0 && row < size - 1
          const isGiven     = given !== 0
          const displayVal  = revealed
            ? puzzle.solution[idx]
            : (isGiven ? given : (user[idx] || ''))

          return (
            <button
              key={idx}
              onClick={() => onSelect(idx)}
              aria-label={`Row ${row + 1}, column ${col + 1}${isGiven ? `, value ${given}` : ''}`}
              aria-pressed={selected === idx}
              style={{
                aspectRatio: '1 / 1',
                background:  getCellBg(idx),
                borderRight:  isBoxRight  ? '2.5px solid #2D2D2D' : '1px solid #D1D5DB',
                borderBottom: isBoxBottom ? '2.5px solid #2D2D2D' : '1px solid #D1D5DB',
                borderLeft: 'none',
                borderTop:  'none',
                cursor:     isGiven ? 'default' : 'pointer',
                display:    'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize,
                fontFamily:  "'Nunito', sans-serif",
                fontWeight:  isGiven || revealed ? 900 : 700,
                color:       getCellColor(idx),
                transition:  'background 0.08s',
              }}
            >
              {displayVal}
            </button>
          )
        })}
      </div>

      {/* Number pad */}
      <div className="flex gap-1.5 justify-center mt-5 flex-wrap">
        {nums.map((n) => (
          <button
            key={n}
            onClick={() => selected >= 0 && puzzle.given[selected] === 0 && onInput(selected, n)}
            className="w-10 h-10 rounded-[12px] border-2 border-bubble-coral bg-white
                       font-display font-black text-bubble-coral text-base
                       hover:bg-bubble-coral-light active:scale-95 transition-all duration-100
                       focus:outline-none focus:ring-2 focus:ring-bubble-coral focus:ring-offset-1
                       disabled:opacity-40"
            disabled={selected < 0 || puzzle.given[selected] !== 0 || revealed}
          >
            {n}
          </button>
        ))}
        <button
          onClick={() => selected >= 0 && puzzle.given[selected] === 0 && onInput(selected, 0)}
          className="w-10 h-10 rounded-[12px] border-2 border-gray-300 bg-white
                     font-display font-black text-bubble-muted text-sm
                     hover:bg-gray-100 active:scale-95 transition-all duration-100
                     focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1
                     disabled:opacity-40"
          aria-label="Clear cell"
          disabled={selected < 0 || puzzle.given[selected] !== 0 || revealed}
        >
          ✕
        </button>
      </div>
    </div>
  )
}
